import { PROCEDURE_TYPE_ALL_VALUE } from '@/components/custom-layout/customer-v2/type';
import { wrapQuote } from '@/lib/utils';
import { AuthenticateInfo } from '@/server/api/v1/auth/types';
import {
    filterEligibleProcedureTypes,
    resolveUserRole,
} from '@/server/api/v1/procedure-type/procedure-type-eligibility';
import { ProcedureTypeResponse } from '@/server/api/v1/procedure-type/type';
import { ProcedureResponse } from '@/server/api/v1/procedure/type';
import {
    ChildLogicalOperator,
    FilterBuilders,
    LogicalOperator,
    OperatorType,
} from '@/server/base/base-trpc-service';
import { ProcedureStatus } from '@/types/enum';

/**
 * Hàm tạo filter lấy danh sách thủ tục cho khách hàng
 * @param authData
 * @returns
 */
export function createFilterProcedureCust(params: {
    authData?: AuthenticateInfo;
    performBy?: string[];
    now: string;
    fieldCodes?: string;
    onlySelf?: boolean;
    procedureTypes?: ProcedureTypeResponse[]
}) {
    const { authData, onlySelf, performBy, fieldCodes } = params;

    let filters: FilterBuilders<ProcedureResponse>[] = [
        {
            key: 'Status',
            values: [ProcedureStatus.TakeEffect],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Equal,
        },
        {
            key: 'Agency_Recv_CODEs',
            values: [''],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.NotEqual,
        },
    ];

    if (fieldCodes) {
        filters.push({
            key: 'FieldCodes',
            values: [fieldCodes],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Regex,
        });
    }

    if (performBy && performBy.length > 0) {
        // Tách sentinel "all" (Tất cả ở personalized khi user không khớp loại
        // nào) ra khỏi các loại thật. Sentinel nghĩa là chỉ lấy thủ tục dùng
        // chung — `Types_Token` rỗng.
        const includesAll = performBy.some(
            (type) => type === PROCEDURE_TYPE_ALL_VALUE
        );
        const realTypes = performBy.filter(
            (type) => type !== PROCEDURE_TYPE_ALL_VALUE
        );

        if (realTypes.length > 0) {
            // Có loại thật → lọc theo `Types_Token` như cũ (bỏ qua sentinel).
            const baseTypes_TokenFilter: FilterBuilders<ProcedureResponse> = {
                key: 'Types_Token',
                values: [''],
                logicalOperator: LogicalOperator.And,
                operatorType: OperatorType.Equal,
                childLogicalOperator: ChildLogicalOperator.Or,
                filters: realTypes.map((type) => ({
                    key: 'Types_Token',
                    values: [wrapQuote(type) ?? type],
                    logicalOperator: LogicalOperator.Or,
                    operatorType: OperatorType.Regex,
                })),
            };

            filters.push(baseTypes_TokenFilter);
        } else if (includesAll) {
            // Chỉ có sentinel "all" → chỉ lấy thủ tục `Types_Token` rỗng.
            filters.push({
                key: 'Types_Token',
                values: [''],
                logicalOperator: LogicalOperator.And,
                operatorType: OperatorType.Equal,
            });
        }
    }
    // Chỉ kiểm tra trạng thái nếu người dùng đã đăng nhập và chỉ muốn
    // lấy thủ tục cho đối tượng bản thân. Dùng helper
    // `filterEligibleProcedureTypes` để áp luật AND giữa
    // `ProcedureTypeStartingCodes` + `RegexBuilderJson` — đồng bộ với
    // submit-dossier.tsx & sub-header-nav-bar.tsx.
    else if (
        onlySelf &&
        params.procedureTypes != null &&
        authData &&
        authData.UserName
    ) {
        const userRole = resolveUserRole(authData);
        const eligibleTypes = filterEligibleProcedureTypes(
            params.procedureTypes.filter(
                (x) => !!x.ValueStr && x.ValueStr.length > 0
            ),
            userRole
        );

        filters.push({
            key: 'Types_Token',
            values: [''],
            logicalOperator: LogicalOperator.And,
            operatorType: OperatorType.Regex,
            childLogicalOperator: ChildLogicalOperator.Or,
            filters: eligibleTypes.map(
                (x) =>
                    ({
                        key: 'Types_Token',
                        values: [x.ValueStr],
                        logicalOperator: LogicalOperator.Or,
                        operatorType: OperatorType.Regex,
                    }) satisfies FilterBuilders<ProcedureResponse>
            ),
        });
    }

    return filters;
}
